import React, { useState, useEffect, useCallback } from "react";
import { useMsal } from "@azure/msal-react";
import { useAuth } from "../AuthProvider";
import { getApiToken } from "../authUtils";
import urlconfig from "../config/urlconfig";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Autocomplete,
  TextField,
  Chip,
  Alert,
} from "@mui/material";
import { catalogTokens, parseApiError } from "./VendorCatalogTheme";
import useCategoryTaxonomy from "./useCategoryTaxonomy";

const fieldSx = {
  "& .MuiOutlinedInput-root": { borderRadius: `${catalogTokens.radius.md}px` },
};

// Metadata-only edit: brand/category/sub-category/collection tags a catalog was missed on at
// upload time. Deliberately doesn't touch price/vendor/the PDF itself — that's what Replace is for.
const VendorCatalogEditDialog = ({ catalog, open, onClose, onSuccess }) => {
  const { instance } = useMsal();
  const { account } = useAuth();
  const { brands, categories, subcategoriesByCategory, ensureSubcategoriesFor, collections } = useCategoryTaxonomy();

  const [selectedCategories, setSelectedCategories] = useState([]);
  const [selectedBrands, setSelectedBrands] = useState([]);
  const [selectedSubcategories, setSelectedSubcategories] = useState([]);
  const [selectedCollections, setSelectedCollections] = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!open || !catalog) return;
    setSelectedCategories(catalog.categories || []);
    setSelectedBrands(catalog.brands || []);
    setSelectedSubcategories((catalog.subCategories || []).map((sc) => ({ categoryName: sc.categoryName, name: sc.name })));
    setSelectedCollections(catalog.collections || []);
    setError(null);
  }, [open, catalog]);

  useEffect(() => {
    ensureSubcategoriesFor(selectedCategories.map((c) => c.name));
    setSelectedSubcategories((prev) => {
      const selectedNames = new Set(selectedCategories.map((c) => c.name));
      const pruned = prev.filter((sc) => selectedNames.has(sc.categoryName));
      return pruned.length === prev.length ? prev : pruned;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCategories]);

  const subcategoryOptions = Object.values(subcategoriesByCategory).flat();
  const collectionGroupLabel = (group) => (group === "OCCASION" ? "Occasion" : "Celebration");

  const sendAuthenticatedRequest = useCallback(
    async (url, method, body) => {
      const token = await getApiToken(instance, account);
      const res = await fetch(url, {
        method,
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
      if (!res.ok) throw new Error(await parseApiError(res));
      const text = await res.text();
      return text ? JSON.parse(text) : {};
    },
    [instance, account]
  );

  const handleSave = async () => {
    setError(null);
    if (selectedCategories.length === 0) return setError("At least one category is required");

    setSaving(true);
    try {
      const updated = await sendAuthenticatedRequest(
        `${urlconfig.backend_api_base_url}/api/v2/vendor-catalogs/${catalog.id}`,
        "PUT",
        {
          brandNames: selectedBrands.map((b) => b.name),
          categoryNames: selectedCategories.map((c) => c.name),
          subCategories: selectedSubcategories.map((sc) => ({ category: sc.categoryName, name: sc.name })),
          collectionIds: selectedCollections.map((c) => c.id),
        }
      );
      onSuccess?.(updated);
    } catch (err) {
      setError(err.message || "Failed to save changes");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 800 }}>Edit catalog tags</DialogTitle>
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2, pt: 1 }}>
        {error && <Alert severity="error" sx={{ borderRadius: `${catalogTokens.radius.md}px` }} onClose={() => setError(null)}>{error}</Alert>}

        <Autocomplete
          multiple
          options={categories}
          getOptionLabel={(option) => option.name || ""}
          isOptionEqualToValue={(o, v) => o?.name === v?.name}
          value={selectedCategories}
          onChange={(_, newVal) => setSelectedCategories(newVal)}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip {...getTagProps({ index })} key={option.name} label={option.name} size="small"
                sx={{ bgcolor: catalogTokens.color.primarySoft, color: catalogTokens.color.primary, fontWeight: 600 }} />
            ))
          }
          renderInput={(params) => <TextField {...params} label="Categories" required sx={fieldSx} />}
        />

        <Autocomplete
          multiple
          options={brands}
          getOptionLabel={(option) => option.name || ""}
          isOptionEqualToValue={(o, v) => o?.name === v?.name}
          value={selectedBrands}
          onChange={(_, newVal) => setSelectedBrands(newVal)}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip {...getTagProps({ index })} key={option.name} label={option.name} size="small"
                sx={{ bgcolor: catalogTokens.color.primarySoft, color: catalogTokens.color.primary, fontWeight: 600 }} />
            ))
          }
          renderInput={(params) => <TextField {...params} label="Brands" placeholder="Optional" sx={fieldSx} />}
        />

        <Autocomplete
          multiple
          options={subcategoryOptions}
          getOptionLabel={(option) => option.name || ""}
          groupBy={(option) => option.categoryName}
          isOptionEqualToValue={(o, v) => o?.categoryName === v?.categoryName && o?.name === v?.name}
          value={selectedSubcategories}
          onChange={(_, newVal) => setSelectedSubcategories(newVal)}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip {...getTagProps({ index })} key={`${option.categoryName}::${option.name}`} label={option.name} size="small"
                sx={{ bgcolor: catalogTokens.color.surfaceMuted, color: catalogTokens.color.textPrimary, fontWeight: 600 }} />
            ))
          }
          renderInput={(params) => (
            <TextField {...params} label="Sub-Categories"
              placeholder={selectedCategories.length ? "Optional" : "Pick a category first"} sx={fieldSx} />
          )}
          disabled={selectedCategories.length === 0}
        />

        <Autocomplete
          multiple
          options={collections}
          getOptionLabel={(option) => option.name || ""}
          groupBy={(option) => collectionGroupLabel(option.group)}
          isOptionEqualToValue={(o, v) => o?.id != null && v?.id != null && String(o.id) === String(v.id)}
          value={selectedCollections}
          onChange={(_, newVal) => setSelectedCollections(newVal)}
          renderTags={(value, getTagProps) =>
            value.map((option, index) => (
              <Chip {...getTagProps({ index })} key={option.id} label={option.name} size="small"
                sx={{ bgcolor: catalogTokens.color.surfaceMuted, color: catalogTokens.color.textPrimary, fontWeight: 600 }} />
            ))
          }
          renderInput={(params) => <TextField {...params} label="Collections / Themes" placeholder="Optional" sx={fieldSx} />}
        />
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} sx={{ textTransform: "none" }}>Cancel</Button>
        <Button
          onClick={handleSave}
          disabled={saving}
          variant="contained"
          disableElevation
          sx={{
            borderRadius: `${catalogTokens.radius.md}px`,
            textTransform: "none",
            fontWeight: 700,
            bgcolor: catalogTokens.color.primary,
            "&:hover": { bgcolor: catalogTokens.color.primaryDark },
          }}
        >
          {saving ? "Saving…" : "Save Changes"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default VendorCatalogEditDialog;
